import React from 'react';
import PropTypes from 'prop-types';

import {connectAppEnv} from '../../../app/app-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {translateToUILevel, withUILevel} from '../../../lib/react/oe-higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import {OEFeatureAccessTypes} from '../../../react-oe/oe-types';
import OENotification from '../../../lib/notification/oe-notification';
import {OEIconButton} from '../../elements/controls/oe-button';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import {oeUniqueIdGen} from '../../../lib/tools/oe-unique-id-gen';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEMainMenuStdBtnType, OEMainMenuStdBtnFeature, OEMainMenuButtonsPropType} from './oe-main-menu-model';

export class OEMainMenuBar extends React.PureComponent {
    
    constructor(props) {
        super(props);

        this.buttonIdCounter = 0;

        this.resizeObserverKey = 'main-menu-resize-observer-' + oeUniqueIdGen.get();

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.buttons = this.constructButtons(this.props, false, true);

        this.state = {
            buttons: this.constructButtonsState()
        };

        this.setListenerToButtons(this);

        this.updateLanguage = this.updateLanguage.bind(this);
        this.onModelStateChanged = this.onModelStateChanged.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);
        this.onExplosionFactorChanged = this.onExplosionFactorChanged.bind(this);
        this.onIsWorkingColorChanged = this.onIsWorkingColorChanged.bind(this);
        this.onLabelsStateChanged = this.onLabelsStateChanged.bind(this);
        this.onCutModeChanged = this.onCutModeChanged.bind(this);
        this.onFeatureAccessChanged = this.onFeatureAccessChanged.bind(this);
        this.onLicensingProductActivationChanged = this.onLicensingProductActivationChanged.bind(this);
        this.onViewOpenStateChanged = this.onViewOpenStateChanged.bind(this);

        this.onAdapterBtnPressed = this.onAdapterBtnPressed.bind(this);
        this.onHomeBtnPressed = this.onHomeBtnPressed.bind(this);
        this.onLabelBtnPressed = this.onLabelBtnPressed.bind(this);
        this.onNoteBtnPressed = this.onNoteBtnPressed.bind(this);
        this.onArrowBtnPressed = this.onArrowBtnPressed.bind(this);
        this.onUserObjectBtnPressed = this.onUserObjectBtnPressed.bind(this);
        this.onCutBtnPressed = this.onCutBtnPressed.bind(this);
        this.onSearchBtnPressed = this.onSearchBtnPressed.bind(this);
        this.onTreeBtnPressed = this.onTreeBtnPressed.bind(this);
        this.onSubstructureBtnPressed = this.onSubstructureBtnPressed.bind(this);
        this.onColorBtnPressed = this.onColorBtnPressed.bind(this);
        this.onExplodeBtnPressed = this.onExplodeBtnPressed.bind(this);
        this.onPresentationBtnPressed = this.onPresentationBtnPressed.bind(this);
        this.onMediaCenterBtnPressed = this.onMediaCenterBtnPressed.bind(this);
        this.onInfoBtnPressed = this.onInfoBtnPressed.bind(this);
        this.onHelpBtnPressed = this.onHelpBtnPressed.bind(this);
        this.onSettingsBtnPressed = this.onSettingsBtnPressed.bind(this);
        this.onBackBtnPressed = this.onBackBtnPressed.bind(this);
        this.onForthBtnPressed = this.onForthBtnPressed.bind(this);
    }

    getNumButtons() {
        return this.buttons.length;
    }

    getButtons()    {
        return this.buttons;
    }

    getButton(id)   {
        this.buttons.find(btn => btn.id === id);
    }

    getButtonVisibility()   {
        let a = this.buttons.map(btn => { return [btn.id, !btn.hidden]; });
        return new Map(a);
    }

    getButtonIdsWithVisibility(visible = true)   {
        let ret = [];
        this.forEachButton(btn => { if(!btn.hidden === visible) ret.push(btn.id); });
        return ret;
    }

    getNumButtonsWithVisibility(visible = true)  {
        let ret = 0;
        this.forEachButton(btn => { if(!btn.hidden === visible) ++ret; });
        return ret;
    }

    changeButtons(fn)   {
        if(typeof(fn) !== 'function')   return;
        if(this.buttonsAreChanging || !this.props.onVisibleButtonsChanged) {
            fn();
            return;
        }
        let buttonVisibility = this.getButtonVisibility();
        this.buttonsAreChanging = true;
        fn();
        this.buttonsAreChanging = false;
        if(!OEToolbox.mapShallowEqual(this.getButtonVisibility(), buttonVisibility))   this.props.onVisibleButtonsChanged(this);
    }

    forEachButton(fn)   {
        this.buttons.forEach(fn);
    }

    findButtonForType(type) {
        if(!type || !this.buttons) return;
        return this.buttons.find(btn => btn.type === type);
    }

    constructButtons(props, activated, disabled) {
        let buttons = (props || this.props).buttons;

        let constructFn = btn => {
            let oldBtnState = this.findButtonForType(btn.type);
            
            let feature = typeof(btn.feature) === 'number' ? btn.feature : OEMainMenuStdBtnFeature[btn.type];

            let btnState = {
                id: typeof(btn.id) === 'number' ? btn.id : this.buttonIdCounter++,
                type: btn.type,
                config: {
                    enabled: btn.enabled,
                    feature: feature,
                    licensingAccess: btn.licensingAccess,
                    tooltip: btn.tooltip,
                    adapter: btn.adapter
                },
                className: btn.className,
                activated: activated,
                disabled: !btn.enabled || disabled,
                hidden: oldBtnState ? oldBtnState.hidden : false,
                featureAvailable: typeof(feature) === 'number' && oldBtnState && feature === oldBtnState.feature ? oldBtnState.featureAvailable : typeof(feature) !== 'number',
                licensingAccessible: true,
                tooltip: '',
                icon: btn.icon
            }

            if(btnState.config.adapter) {
                btnState.activated = btnState.config.adapter.isActive(this);
                btnState.disabled = !(btnState.config.enabled && btnState.config.adapter.isEnabled(this));
                btnState.featureAvailable = btnState.config.adapter.isFeatureAvailable(this);
                btnState.licensingAccessible = btnState.config.adapter.isLicensingAccessible(this);
            }
            return btnState;
        };

        return buttons.map(constructFn);
    }

    constructButtonsState() {
        let constructFn = btn => { return {
            id: btn.id,
            type: btn.type,
            className: typeof(btn.className) === 'string' ? btn.className : '',
            activated: btn.activated,
            disabled: btn.disabled,
            hidden: btn.hidden,
            featureAvailable: btn.featureAvailable,
            licensingAccessible: btn.licensingAccessible,
            tooltip: btn.tooltip,
            icon: btn.icon
        }};

        return this.buttons.map(constructFn);
    }

    setListenerToButtons(listener) {
        this.forEachButton(btn => {
            if(btn.config.adapter) btn.config.adapter.menu = listener;
        });
    }

    connectButtons()   {
        if(!this.oe.isReady()) return;
        this.forEachButton(btn => {
            if(!btn.config.adapter) return;
            let adapter = btn.config.adapter;

            if(adapter._connectedModuleId)  {
                adapter.onRelease(this.oe, this);
            }
            
            adapter.oe = this.oe;
            adapter._connectedModuleId = this.oe.moduleId;
            adapter.onConnect(this.oe, this);
        });
    }

    releaseButtons()   {
        this.forEachButton(btn => {
            if(!btn.config.adapter) return;
            let adapter = btn.config.adapter;
            if(!adapter._connectedModuleId)  return;
            adapter.onRelease(this.oe, this);
            adapter._connectedModuleId = undefined;
        });
    }

    componentWillReceiveProps(nextProps) {
        if(!OEToolbox.arrayShallowEqual(this.props.buttons, nextProps.buttons) || this.props.uiLevel !== nextProps.uiLevel) {
            this.changeButtons(() => {
                this.setListenerToButtons(null);
                this.releaseButtons();
                this.buttons = this.constructButtons(nextProps, false, true);
                this.updateButtonsState();
                this.setListenerToButtons(this);
                this.connectButtons();
                this.updateTooltips();
                this.updateButtons(nextProps, false, true);
            });
        }
    }

    componentDidMount()    {
        this.setListenerToButtons(this);
    }

    componentWillUnmount()    {
        this.setListenerToButtons(null);
    }

    onConnect()  {
        this.connectButtons();
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.newModelStateSaved, this.onModelStateChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.modelStateWasApplied, this.onModelStateChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.modelStateCollectionWasReset, this.onModelStateChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.explosionFactorChanged, this.onExplosionFactorChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.isWorkingColorChanged, this.onIsWorkingColorChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.labelsStateChanged, this.onLabelsStateChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.cutModeChanged, this.onCutModeChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.featureAccessChanged, this.onFeatureAccessChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.licensingProductActivationChanged, this.onLicensingProductActivationChanged);

        this.props.appComponent.uiLayer.notificationCenter.register(OENotification.viewOpenStateChanged, this.onViewOpenStateChanged);
    }

    onRelease()    {
        this.releaseButtons();
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.newModelStateSaved, this.onModelStateChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.modelStateWasApplied, this.onModelStateChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.modelStateCollectionWasReset, this.onModelStateChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.explosionFactorChanged, this.onExplosionFactorChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.isWorkingColorChanged, this.onIsWorkingColorChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.labelsStateChanged, this.onLabelsStateChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.cutModeChanged, this.onCutModeChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.featureAccessChanged, this.onFeatureAccessChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.licensingProductActivationChanged, this.onLicensingProductActivationChanged);

        this.props.appComponent.uiLayer.notificationCenter.unregister(OENotification.viewOpenStateChanged, this.onViewOpenStateChanged);
    }

    updateLanguage()   {
        this.updateTooltips();
        this.updateButtonsState();
    }

    isLabelWidgetEnabled(props)  {
        props = props || this.props;
        return !props.widgetConfig || translateToUILevel(props.widgetConfig.labelController, props.uiLevel).enabled;
    }

    isCutWidgetEnabled(props)  {
        props = props || this.props;
        return !props.widgetConfig || translateToUILevel(props.widgetConfig.cutController, props.uiLevel).enabled;
    }

    updateTooltips()    {
        if(!this.oe.isReady()) return;
        let si = this.oe.sharedInterface;
        this.forEachButton(btn => {
            btn.tooltip = si.getLocalizedStringEnc(btn.config.tooltip ? btn.config.tooltip : ('main_menu_tooltip_' + OEToolbox.lowerCamelCaseToLowercaseUnderscore(btn.type)));
        });
    }

    updateForthBackButtons()  {
        if(!this.oe.isReady()) return;

        let modelStateController = this.oe.sharedInterface.getUIControllerModelState();

        const uiEnabled = modelStateController.getUIEnabled();
        const frontDis = modelStateController.frontDistance();
        const backDis = modelStateController.backDistance();

        let backBtn = this.findButtonForType(OEMainMenuStdBtnType.back);
        if(backBtn) {
            backBtn.disabled = !(backBtn.config.enabled && backDis > 0 && uiEnabled);
            if(backBtn.config.adapter)  {
                backBtn.activated = backBtn.config.adapter.isActive(this);
                backBtn.disabled = !(backBtn.config.enabled && backBtn.config.adapter.isEnabled(this));
            }
        }

        let forthBtn = this.findButtonForType(OEMainMenuStdBtnType.forth);
        if(forthBtn) {
            forthBtn.disabled = !(forthBtn.config.enabled && frontDis > 0 && uiEnabled);
            if(forthBtn.config.adapter)  {
                forthBtn.activated = forthBtn.config.adapter.isActive(this);
                forthBtn.disabled = !(forthBtn.config.enabled && forthBtn.config.adapter.isEnabled(this));
            }
        }
    }

    updateButtons(props, released, muteCallback) {
        if(!this.oe.isReady() || released)  {
            // do not overwrite hidden state here
            this.buttons = this.constructButtons(props, false, true);
            this.updateButtonsState();
            return;
        }

        props = props || this.props;

        const label = this.oe.sharedInterface.getUIControllerLabel();
        const note = this.oe.sharedInterface.getUIControllerNote();
        const arrow = this.oe.sharedInterface.getUIControllerArrow();
        const userObject = this.oe.sharedInterface.getUIControllerUserObject();
        const cut = this.oe.sharedInterface.getUIControllerCut();
        const search = this.oe.sharedInterface.getUIControllerSearch();
        const component = this.oe.sharedInterface.getUIControllerComponent();
        const substructure = this.oe.sharedInterface.getUIControllerSubstructure();
        const color = this.oe.sharedInterface.getUIControllerColor();
        const explosion = this.oe.sharedInterface.getUIControllerExplosion();
        const presentation = this.oe.sharedInterface.getUIControllerPresentation();
        const mediaCenter = this.oe.sharedInterface.getUIControllerMediaCenter();
        const info = this.oe.sharedInterface.getUIControllerInfo();
        const settings = this.oe.sharedInterface.getUIControllerSettings();

        let buttons = {
            home: {activated: false, disabled: false},
            label: this.isLabelWidgetEnabled(props) ? {activated: label.getUIVisible(), disabled: !label.getUIPresentable()} : {activated: label.getEnabled(), disabled: !label.getUIEnabled()},
            note: {activated: note.getUIVisible(), disabled: !note.getUIPresentable()},
            arrow: {activated: arrow.getUIVisible(), disabled: !arrow.getUIPresentable()},
            userObject: {activated: userObject.getUIVisible(), disabled: !userObject.getUIPresentable()},
            cut: this.isCutWidgetEnabled(props) ? {activated: cut.getUIVisible(), disabled: !cut.getUIPresentable()} : {activated: cut.getMode() !== this.oe.Module.CutMode.disabled, disabled: !cut.getUIEnabled()},
            search: {activated: search.getUIVisible(), disabled: !search.getUIPresentable()},
            tree: {activated: component.getUIVisible(), disabled: !component.getUIPresentable()},
            substructure: {activated: substructure.getUIVisible(), disabled: !substructure.getUIPresentable(), hidden: substructure.numClasses() == 0},
            color: {activated: color.getIsWorkingColor(), disabled: !color.getUIEnabled()},
            explode: {activated: explosion.isExploded(), disabled: !explosion.getUIEnabled()},
            presentation: {activated: presentation.getUIVisible(), disabled: !presentation.getUIPresentable()},
            mediaCenter: {activated: mediaCenter.getUIVisible(), disabled: !mediaCenter.getUIPresentable()},
            info: {activated: info.getUIVisible(), disabled: !info.getUIPresentable()},
            settings: {activated: settings.getUIVisible(), disabled: !settings.getUIPresentable()}
        };

        let hiddenChanged = false;

        this.forEachButton(btn => {
            let btn_ = buttons[btn.type];
            if(btn_) {
                btn.activated = btn_.activated;
                btn.disabled = !btn.config.enabled || btn_.disabled;
                if(typeof(btn_.hidden) === 'boolean' && btn.hidden !== btn_.hidden) {
                    btn.hidden = btn_.hidden;
                    hiddenChanged = true;
                }
            }
            if(!btn.config.adapter) return;
            btn.activated = btn.config.adapter.isActive(this);
            btn.disabled = !(btn.config.enabled && btn.config.adapter.isEnabled(this));
            btn.featureAvailable = btn.config.adapter.isFeatureAvailable(this);
            btn.licensingAccessible = btn.config.adapter.isLicensingAccessible(this);
        });

        if(!muteCallback && hiddenChanged && props.onVisibleButtonsChanged) props.onVisibleButtonsChanged(this);
        
        this.updateFeatureAvailable();

        this.updateLicensingAccessibility();

        this.updateForthBackButtons();

        this.updateButtonsState();
    }

    updateButtonsState() {
        this.setState({buttons: this.constructButtonsState()});
    }

    updateFeatureAvailable()    {
        let featureSet = this.oe.sharedInterface.getUIControllerFeatureAccess().getFeatureSet();

        this.forEachButton(btn => {
            if(btn.config.adapter) return;
            let feature = btn.config.feature;
            btn.featureAvailable = typeof(feature) === 'number' ? featureSet.hasFeature({value: feature}) : true;
        });

        featureSet.delete();
    }

    updateLicensingAccessibility()    {
        let isLicensing = config.accessType == OEFeatureAccessTypes.licensing;
        let si = this.oe.sharedInterface;

        if(!isLicensing || !si.getUIControllerFeatureAccess().isAccessType(this.oe.Module.FeatureAccessType.licensing))  {
            this.forEachButton(btn => {
                if(btn.config.adapter) return;
                btn.licensingAccessible = true;
            });
            return;
        }

        let licenseValid = si.getUIControllerLicensingManager().isValid();
        let edition = si.getUIControllerLicensingManager().getEdition();

        this.forEachButton(btn => {
            if(btn.config.adapter) return;
            btn.licensingAccessible = btn.config.licensingAccess ? btn.config.licensingAccess(licenseValid, edition, this.oe) : true;
        });

    }

    updateForAdapter(adapter)  {
        let btn = this.buttons.find(btn => btn.config.adapter === adapter);
        if(!btn)    return;
        if(!btn.config.adapter) return;
        btn.activated = btn.config.adapter.isActive(this);
        btn.disabled = !(btn.config.enabled && btn.config.adapter.isEnabled(this));
        btn.featureAvailable = btn.config.adapter.isFeatureAvailable(this);
        btn.licensingAccessible = btn.config.adapter.isLicensingAccessible(this);
        this.updateButtonsState();
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true) {
            // do not overwrite hidden state here
            this.updateButtons(undefined, released);
            return;
        }

        retardUpdate(this, () => {
            this.updateLanguage();
            this.updateButtons();
        });
    }

    onModelStateChanged()   {
        this.updateForthBackButtons();
        this.updateButtonsState();
    }
    
    onUIControllerStateChanged(message, userInfo)   {
        if(userInfo.type === this.oe.Module.UIControllerType.model_state)   {
            this.updateForthBackButtons();
            this.updateButtonsState();
            return;
        }

        let btnType = null;
        switch(userInfo.type)   {
            case this.oe.Module.UIControllerType.label: btnType = OEMainMenuStdBtnType.label; break;
            case this.oe.Module.UIControllerType.note: btnType = OEMainMenuStdBtnType.note; break;
            case this.oe.Module.UIControllerType.arrow: btnType = OEMainMenuStdBtnType.arrow; break;
            case this.oe.Module.UIControllerType.user_object: btnType = OEMainMenuStdBtnType.userObject; break;
            case this.oe.Module.UIControllerType.cut: btnType = OEMainMenuStdBtnType.cut; break;
            case this.oe.Module.UIControllerType.search_controller: btnType = OEMainMenuStdBtnType.search; break;
            case this.oe.Module.UIControllerType.component: btnType = OEMainMenuStdBtnType.tree; break;
            case this.oe.Module.UIControllerType.substructure: btnType = OEMainMenuStdBtnType.substructure; break;
            case this.oe.Module.UIControllerType.color: btnType = OEMainMenuStdBtnType.color; break;
            case this.oe.Module.UIControllerType.explosion: btnType = OEMainMenuStdBtnType.explode; break;
            case this.oe.Module.UIControllerType.presentation: btnType = OEMainMenuStdBtnType.presentation; break;
            case this.oe.Module.UIControllerType.media_center: btnType = OEMainMenuStdBtnType.mediaCenter; break;
            case this.oe.Module.UIControllerType.info: btnType = OEMainMenuStdBtnType.info; break;
            case this.oe.Module.UIControllerType.settings: btnType = OEMainMenuStdBtnType.settings; break;
        }

        let btn = this.findButtonForType(btnType);
        if(!btn || btn.config.adapter) return;

        let isFunctionalBtn = 
            (btnType === OEMainMenuStdBtnType.label && !this.isLabelWidgetEnabled()) ||
            (btnType === OEMainMenuStdBtnType.cut && !this.isCutWidgetEnabled()) ||
            (btnType === OEMainMenuStdBtnType.color || btnType === OEMainMenuStdBtnType.explode);

        if(!isFunctionalBtn)    {
            btn.activated = userInfo.visible;
            btn.disabled = !(btn.config.enabled && userInfo.presentable);
        } else {
            btn.disabled = !userInfo.enabled;
        }
        
        this.updateButtonsState();
    }
    
    onExplosionFactorChanged(message, userInfo)   {
        let btn = this.findButtonForType(OEMainMenuStdBtnType.explode);
        if(!btn) return;
        btn.activated = userInfo.exploded;
        this.updateButtonsState();
    }
    
    onIsWorkingColorChanged(message, userInfo)   {
        let btn = this.findButtonForType(OEMainMenuStdBtnType.color);
        if(!btn) return;
        btn.activated = userInfo.isWorkingColor;
        this.updateButtonsState();
    }

    onLabelsStateChanged(message, userInfo)    {
        if(this.isLabelWidgetEnabled()) return;
        let btn = this.findButtonForType(OEMainMenuStdBtnType.label);
        if(!btn) return;
        btn.activated = userInfo.enabled;
        this.updateButtonsState();
    }

    onCutModeChanged(message, userInfo)  {
        if(this.isCutWidgetEnabled()) return;
        let btn = this.findButtonForType(OEMainMenuStdBtnType.cut);
        if(!btn) return;
        btn.activated = userInfo.mode !== this.oe.Module.CutMode.disabled;
        this.updateButtonsState();
    }

    onFeatureAccessChanged(message, userInfo)  {
        this.updateFeatureAvailable();
        this.updateButtonsState();
    }

    onLicensingProductActivationChanged(message, userInfo)  {
        this.updateLicensingAccessibility();
        this.updateButtonsState();
    }

    onViewOpenStateChanged(message, userInfo)   {
        this.updateButtons();
    }

    renderButton(btn)  {
        let type = btn.type;
        let typeLU = OEToolbox.lowerCamelCaseToLowercaseUnderscore(type, '-');
        let typeUC = OEToolbox.capitalize(type);
        let iconCode = (type == OEMainMenuStdBtnType.back || type == OEMainMenuStdBtnType.forth) ? ('state' + typeUC) : type;
        let btnIdentifierStr = 'mm-' + typeLU + '-btn';

        return (
            <OEIconButton
                id={btnIdentifierStr}
                className={this.props.buttonClassName + ' ' + btnIdentifierStr + ' ' + btn.className + ((!btn.featureAvailable || !btn.licensingAccessible) ? ' disabled' : '')}
                activated={!btn.disabled && btn.activated}
                disabled={btn.disabled && btn.featureAvailable && btn.licensingAccessible}
                onPressed={this.onAdapterBtnPressed}
                tooltip={btn.tooltip}
                tooltipPlacement={this.props.tooltipPlacement}
                icon={btn.icon || OEIconCodes[iconCode]}
                userData={btn}
            />
        );
    }

    render() {
        const fn = (btn) => {
            let typeLU = OEToolbox.lowerCamelCaseToLowercaseUnderscore(btn.type);
            let style = {}; if(btn.hidden) style.display = 'none';
            return <li key={typeLU} style={style}>{this.renderButton(btn)}</li>;
        };
        const buttons = this.state.buttons.map(fn);

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>

                <div
                    className={'main-menu-bar ' + this.props.className}
                    style={this.props.style}
                    ref={this.props.elementRef}
                >
                    {this.props.onElementResize ? <OEResizeObserver key={this.resizeObserverKey} onResize={this.props.onElementResize}/> : null}

                    <ul>{buttons}</ul>
                </div>

            </React.Fragment>
        );
    }

    onAdapterBtnPressed(e, sender)   {
        let btn = this.findButtonForType(sender.props.userData.type);

        if(btn.disabled)    {
            if(this.oe.isReady() && (!btn.featureAvailable || !btn.licensingAccessible) && this.props.appComponent.uiLayer.freePremiumView)   {
                this.props.appComponent.uiLayer.freePremiumView.setOpen(true, {feature: btn.config.feature});
            }
            return;
        }

        let adapter = btn.config.adapter;
        if(!adapter) {
            let typeUC = OEToolbox.capitalize(btn.type);
            if(this['on' + typeUC + 'BtnPressed']) this['on' + typeUC + 'BtnPressed']();
            return false;
        }
        btn.activated = adapter.onPressed(sender.props.activated, this);
        this.updateButtonsState();
        return true;
    }

    onHomeBtnPressed()  {
        this.props.appComponent.app.reset();
    }
    
    onLabelBtnPressed() {
        if(!this.oe.isReady()) return;

        if(!this.isLabelWidgetEnabled()) {
            let label = this.oe.sharedInterface.getUIControllerLabel();
            label.setEnabled(!label.getEnabled(), true, 0.333);
            return;
        }

        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.label), this.oe.Module.UIControllerType.label))   {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.label, true);
        }
    }

    onNoteBtnPressed()   {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.note), this.oe.Module.UIControllerType.note))   {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.note, true);
        }
    }

    onArrowBtnPressed()   {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.arrow), this.oe.Module.UIControllerType.arrow))   {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.arrow, true);
        }
    }

    onUserObjectBtnPressed()   {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.user_object), this.oe.Module.UIControllerType.user_object))   {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.user_object, true);
        }
    }
    
    onCutBtnPressed() {
        if(!this.oe.isReady()) return;

        if(!this.isCutWidgetEnabled()) {
            let cut = this.oe.sharedInterface.getUIControllerCut(); const CutMode = this.oe.Module.CutMode;
            cut.setMode(cut.getMode() !== CutMode.disabled ? CutMode.disabled : CutMode.touch);
            return;
        }

        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.cut), this.oe.Module.UIControllerType.cut))   {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.cut, true);
        }
    }

    onSearchBtnPressed() {
        if(!this.oe.isReady()) return;

        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.search_controller), this.oe.Module.UIControllerType.search_controller))   {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.search_controller, true);
        }
    }
    
    onTreeBtnPressed() {
        if(!this.oe.isReady()) return;
        this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.component), this.oe.Module.UIControllerType.component);
    }

    onSubstructureBtnPressed() {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.substructure), this.oe.Module.UIControllerType.substructure)) {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.substructure, true);
        }
    }
    
    onColorBtnPressed() {
        if(!this.oe.isReady()) return;
        let btn = this.findButtonForType(OEMainMenuStdBtnType.color);
        this.oe.sharedInterface.getUIControllerColor().setIsWorkingColor(!btn.activated, true, 0.333);
    }
    
    onExplodeBtnPressed() {
        if(!this.oe.isReady()) return;
        let btn = this.findButtonForType(OEMainMenuStdBtnType.explode);
        this.oe.sharedInterface.getUIControllerExplosion().setExplosionFactorStd(btn.activated ? 0.0 : 1.0, true);
    }
    
    onPresentationBtnPressed() {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.presentation), this.oe.Module.UIControllerType.presentation)) {
            if(this.props.widgetConfig && this.props.widgetConfig.presentationController.enabled) {
                this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.presentation, true);
            }
        }
    }

    onMediaCenterBtnPressed()   {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.media_center), this.oe.Module.UIControllerType.media_center)) {
            //this.oe.sharedInterface.setUIControllerVisible(false, this.oe.Module.UIControllerType.presentation);
        }
    }

    onInfoBtnPressed(e, sender) {
        let appComponent = this.props.appComponent;
        if(appComponent.uiLayer.infoView)   appComponent.uiLayer.infoView.setOpen(true);
    }

    onHelpBtnPressed(e, sender) {
    }

    onSettingsBtnPressed() {
        if(!this.oe.isReady()) return;
        if(this.oe.sharedInterface.setUIControllerVisible(!this.oe.sharedInterface.getUIControllerVisible(this.oe.Module.UIControllerType.settings), this.oe.Module.UIControllerType.settings))    {
            this.props.appComponent.uiLayer.closeAllPopovers(this.oe.Module.UIControllerType.settings, true);
        }
    }
    
    onBackBtnPressed() {
        if(!this.oe.isReady()) return;
        this.oe.sharedInterface.getUIControllerModelState().inc(-1);
    }
    
    onForthBtnPressed() {
        if(!this.oe.isReady()) return;
        this.oe.sharedInterface.getUIControllerModelState().inc(1);
    }
}

OEMainMenuBar.defaultProps = {
    className:                  '',
    moduleId:                   '',
    buttonClassName:            '',
    buttons:                    [],
    tooltipPlacement:           'right'
};

OEMainMenuBar.propTypes = {
    className:                  PropTypes.string,
    style:                      PropTypes.object,
    moduleId:                   PropTypes.string,
    buttonClassName:            PropTypes.string,
    buttons:                    OEMainMenuButtonsPropType,
    tooltipPlacement:           PropTypes.string,
    onVisibleButtonsChanged:    PropTypes.func,
    elementRef:                 PropTypes.func,
    onElementResize:            PropTypes.func
};

export default frefFromRef(connectAppEnv((env) => {
    const ui = env.config.module.uiLayerConfig;
    return {
        appComponent:           env.component,
        widgetConfig:           ui.widgetConfig
    };
})(withUILevel(frefToRef(OEMainMenuBar))));